import Client from '@/api/client'
import { getConfig } from '@/app/config'

import { ManualQuote, ManualQuoteStatus } from '../domain/manual-quote'

const client = new Client(getConfig().API_ENDPOINT ?? '')

type ManualQuotesSearchResult = ManualQuote[]

export const getManualQuotes = async (): Promise<ManualQuote[]> => {
  return await client.get<ManualQuotesSearchResult>('/api/shipping/api/v1/quotes/manualQuotes')
}

export const withdrawManualQuote = async ({ id, customerId }: { id: string; customerId: string }): Promise<void> => {
  return await client.put<void>(`/api/quotation/manualQuotes/${id}/withdraw?customerId=${customerId}`)
}

export enum ManualQuoteListFilterType {
  ALL = 'all',
  PENDING = 'pending',
  VALID = 'valid',
  EXPIRED = 'expired',
  DECLINED = 'declined',
  WITHDRAWN = 'withdrawn',
  NOT_AVAILABLE = 'notAvailable',
  RECENT = 'recent',
}

export type QuoteFilterKey =
  | 'quotes.status.all'
  | 'quotes.status.pending'
  | 'quotes.status.valid'
  | 'quotes.status.expired'
  | 'quotes.status.declined'
  | 'quotes.status.withdrawn'
  | 'quotes.status.notAvailable'
  | 'quotes.status.recent'

export const getFilters = (): { name: ManualQuoteListFilterType; labelKey: QuoteFilterKey }[] => [
  { name: ManualQuoteListFilterType.ALL, labelKey: 'quotes.status.all' },
  { name: ManualQuoteListFilterType.PENDING, labelKey: 'quotes.status.pending' },
  { name: ManualQuoteListFilterType.VALID, labelKey: 'quotes.status.valid' },
  { name: ManualQuoteListFilterType.EXPIRED, labelKey: 'quotes.status.expired' },
  { name: ManualQuoteListFilterType.DECLINED, labelKey: 'quotes.status.declined' },
  { name: ManualQuoteListFilterType.WITHDRAWN, labelKey: 'quotes.status.withdrawn' },
  { name: ManualQuoteListFilterType.RECENT, labelKey: 'quotes.status.recent' },
  { name: ManualQuoteListFilterType.NOT_AVAILABLE, labelKey: 'quotes.status.notAvailable' },
]

export const isQuoteSheetPending = (quoteSheet: ManualQuote): boolean => {
  const allowedStatuses: ManualQuoteStatus[] = ['open', 'pending', 'requested']

  return allowedStatuses.some((item) => item === quoteSheet.request.manualQuoteRequest?.status)
}

export const isQuoteSheetValid = (quoteSheet: ManualQuote): boolean => {
  const allowedStatuses: ManualQuoteStatus[] = ['ready', 'booked', 'sent']

  return (
    allowedStatuses.some((item) => item === quoteSheet.request.manualQuoteRequest?.status) &&
    new Date(quoteSheet.response.validUntil) > new Date()
  )
}

export const isQuoteSheetExpired = (quoteSheet: ManualQuote): boolean => {
  return quoteSheet.request.manualQuoteRequest?.status === 'expired'
}

export const isQuoteSheetDeclined = (quoteSheet: ManualQuote): boolean => {
  return quoteSheet.request.manualQuoteRequest?.status === 'declinedByCustomer'
}

export const isQuoteSheetNotAvailable = (quoteSheet: ManualQuote): boolean => {
  return quoteSheet.request.manualQuoteRequest?.status === 'declinedByFreighthub'
}

export const isQuoteSheetWithdrawn = (quoteSheet: ManualQuote): boolean => {
  return quoteSheet.request.manualQuoteRequest?.status === 'withdrawn'
}

const filterMap: Record<ManualQuoteListFilterType, (quoteSheet: ManualQuote) => boolean> = {
  [ManualQuoteListFilterType.ALL]: () => true,
  [ManualQuoteListFilterType.PENDING]: isQuoteSheetPending,
  [ManualQuoteListFilterType.VALID]: isQuoteSheetValid,
  [ManualQuoteListFilterType.EXPIRED]: isQuoteSheetExpired,
  [ManualQuoteListFilterType.DECLINED]: isQuoteSheetDeclined,
  [ManualQuoteListFilterType.NOT_AVAILABLE]: isQuoteSheetNotAvailable,
  [ManualQuoteListFilterType.WITHDRAWN]: isQuoteSheetWithdrawn,
  [ManualQuoteListFilterType.RECENT]: () => true,
}

export const getFilteredQuoteSheetsByFilterType = (
  quoteSheets: ManualQuote[],
  filterType: ManualQuoteListFilterType
) => {
  return quoteSheets.filter(filterMap[filterType] || (() => true))
}
